import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SEARCH_TIMEOUT_MS = 5000;
const LAYOUT_STABLE_MS = 400;
const USER_SCROLL_CANCEL_THRESHOLD_PX = 20;

export const useEmployeeProfileAnchor = () => {
  const location = useLocation();

  useEffect(() => {
    let anchor: string;

    try {
      anchor = decodeURIComponent(location.hash.slice(1));
    } catch {
      return;
    }

    if (!anchor) return;

    const startedAt = performance.now();
    let searchFrameId = 0;
    let stableTimeoutId = 0;
    let maxTimeoutId = 0;
    let resizeObserver: ResizeObserver | undefined;
    let mutationObserver: MutationObserver | undefined;
    let highlightAnimations: Animation[] = [];
    let highlightElement: HTMLSpanElement | undefined;
    let highlightedTarget: HTMLElement | undefined;
    let originalTargetPosition = "";
    let scrollContainer: HTMLElement | undefined;
    let userScrollDistance = 0;
    let lastTouchY: number | undefined;
    let cancelled = false;
    let highlighted = false;

    const clearHighlight = () => {
      highlightAnimations.forEach((animation) => animation.cancel());
      highlightAnimations = [];
      highlightElement?.remove();
      if (highlightedTarget) highlightedTarget.style.position = originalTargetPosition;
    };

    const cancelForcedScroll = () => {
      if (!scrollContainer || cancelled) return;

      cancelled = true;
      scrollContainer.scrollTo({ top: scrollContainer.scrollTop, behavior: "auto" });
      disconnectObservers();
    };

    const handleWheel = (event: WheelEvent) => {
      userScrollDistance += Math.abs(event.deltaY);
      if (userScrollDistance > USER_SCROLL_CANCEL_THRESHOLD_PX) cancelForcedScroll();
    };

    const handleTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touchY = event.touches[0]?.clientY;
      if (lastTouchY === undefined || touchY === undefined) return;

      userScrollDistance += Math.abs(touchY - lastTouchY);
      lastTouchY = touchY;
      if (userScrollDistance > USER_SCROLL_CANCEL_THRESHOLD_PX) cancelForcedScroll();
    };

    const disconnectObservers = () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      scrollContainer?.removeEventListener("wheel", handleWheel);
      scrollContainer?.removeEventListener("touchstart", handleTouchStart);
      scrollContainer?.removeEventListener("touchmove", handleTouchMove);
      window.clearTimeout(stableTimeoutId);
      window.clearTimeout(maxTimeoutId);
    };

    const observeLayout = (target: HTMLElement) => {
      scrollContainer =
        target.closest<HTMLElement>("[data-employee-scroll-container]") ?? undefined;
      if (!scrollContainer) return;

      const highlightTarget = () => {
        if (highlighted || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        const tableHeading = target.querySelector<HTMLElement>("[data-employee-table-heading]");
        if (!tableHeading) return;

        highlighted = true;
        highlightedTarget = tableHeading;
        originalTargetPosition = tableHeading.style.position;
        if (getComputedStyle(tableHeading).position === "static") {
          tableHeading.style.position = "relative";
        }

        highlightElement = document.createElement("span");
        Object.assign(highlightElement.style, {
          position: "absolute",
          inset: "0",
          background:
            "linear-gradient(110deg, transparent 20%, hsl(var(--var-color-brand-dark) / 0.08) 42%, hsl(var(--var-color-brand-dark) / 0.18) 50%, hsl(var(--var-color-brand-dark) / 0.08) 58%, transparent 80%)",
          backgroundSize: "220% 100%",
          backgroundRepeat: "no-repeat",
          borderRadius: "inherit",
          pointerEvents: "none",
          zIndex: "1",
        });
        tableHeading.append(highlightElement);

        const waveAnimation = highlightElement.animate(
          [{ backgroundPosition: "150% 0" }, { backgroundPosition: "-150% 0" }],
          { duration: 3000, easing: "ease-in-out" }
        );
        highlightAnimations.push(waveAnimation);
        waveAnimation.addEventListener("finish", clearHighlight, { once: true });
      };

      scrollContainer.addEventListener("wheel", handleWheel, { passive: true });
      scrollContainer.addEventListener("touchstart", handleTouchStart, { passive: true });
      scrollContainer.addEventListener("touchmove", handleTouchMove, { passive: true });

      const align = (behavior: ScrollBehavior = "auto") => {
        if (cancelled) return;
        target.scrollIntoView({ behavior, block: "center" });
        window.clearTimeout(stableTimeoutId);
        stableTimeoutId = window.setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          highlightTarget();
        }, LAYOUT_STABLE_MS);
      };

      resizeObserver = new ResizeObserver(() => align());
      resizeObserver.observe(target);
      Array.from(scrollContainer.children).forEach((child) => {
        if (child instanceof HTMLElement && child !== target) resizeObserver?.observe(child);
      });

      mutationObserver = new MutationObserver(() => align());
      mutationObserver.observe(scrollContainer, { childList: true, subtree: true });

      maxTimeoutId = window.setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        highlightTarget();
        disconnectObservers();
      }, SEARCH_TIMEOUT_MS);

      align();
    };

    const findTarget = () => {
      const target = document.getElementById(anchor);
      if (target) {
        observeLayout(target);
        return;
      }

      if (performance.now() - startedAt < SEARCH_TIMEOUT_MS) {
        searchFrameId = requestAnimationFrame(findTarget);
      }
    };

    searchFrameId = requestAnimationFrame(findTarget);
    return () => {
      cancelAnimationFrame(searchFrameId);
      disconnectObservers();
      clearHighlight();
    };
  }, [location.hash, location.key]);
};
